package com.writerslab

import kotlinx.browser.window

// ─── Firebase JS SDK bindings ─────────────────────────────────────────
// Modular v10+ API: each entry point is its own subpath import. Kotlin/JS
// resolves these through the `firebase` npm dep declared in build.gradle.
// We only need analytics + app for now; auth/firestore/etc. stay
// uninstalled until something actually uses them.

@JsModule("firebase/app")
@JsNonModule
private external object FirebaseApp {
    fun initializeApp(config: dynamic): dynamic
}

@JsModule("firebase/analytics")
@JsNonModule
private external object FirebaseAnalyticsLib {
    fun getAnalytics(app: dynamic = definedExternally): dynamic
    fun logEvent(analytics: dynamic, name: String, params: dynamic = definedExternally)
    fun setUserId(analytics: dynamic, id: String?)
    fun setUserProperties(analytics: dynamic, properties: dynamic)
}

/**
 * Configuration for Firebase Analytics. Values come from
 * [BuildFirebaseConfig], which Gradle generates from the root
 * `local.properties` (`FIREBASE_*` keys) on every compile.
 */
data class FirebaseConfig(
    val apiKey: String,
    val authDomain: String,
    val projectId: String,
    val storageBucket: String,
    val messagingSenderId: String,
    val appId: String,
    val measurementId: String?,
)

/**
 * Thin facade over Firebase Analytics.
 *
 * Init contract: when [init] succeeds the facade flips to live mode and
 * subsequent [event] / [setUserId] / [setUserProperty] / [recordError]
 * calls forward to the JS SDK. If init never lands (missing config,
 * SDK throws) the calls turn into no-ops — boot never breaks because
 * Analytics couldn't init, and callers don't have to gate themselves
 * on init state.
 *
 * Tests don't touch this object directly: [DefaultAnalyticsEvents]
 * accepts an [AnalyticsSink] via constructor, so unit tests substitute
 * a recording fake without involving Firebase at all.
 */
object Analytics {
    private var initialized: Boolean = false
    private var analyticsHandle: dynamic = null

    /** Wire the SDK against a project. Safe to call more than once;
     *  the second + calls are ignored, matching what the real SDK
     *  does internally. If the SDK throws (bad config) the facade
     *  stays inert — boot never breaks because Analytics couldn't init. */
    fun init(config: FirebaseConfig) {
        if (initialized) return
        initialized = true
        try {
            val js = js("({})")
            js.apiKey = config.apiKey
            js.authDomain = config.authDomain
            js.projectId = config.projectId
            js.storageBucket = config.storageBucket
            js.messagingSenderId = config.messagingSenderId
            js.appId = config.appId
            if (config.measurementId != null) js.measurementId = config.measurementId
            val app = FirebaseApp.initializeApp(js)
            analyticsHandle = FirebaseAnalyticsLib.getAnalytics(app)
        } catch (_: Throwable) {
            analyticsHandle = null
        }
    }

    /** Bind events to a logged-in user. Called from MainViewModel after
     *  `loadUserInfo()` returns; cleared on signOut by passing `null`. */
    fun setUserId(id: String?) {
        val handle = analyticsHandle ?: return
        runCatching { FirebaseAnalyticsLib.setUserId(handle, id) }
    }

    fun setUserProperty(name: String, value: String?) {
        val handle = analyticsHandle ?: return
        runCatching {
            val js = js("({})")
            js[name] = value
            FirebaseAnalyticsLib.setUserProperties(handle, js)
        }
    }

    fun event(name: String, params: Map<String, Any?> = emptyMap()) {
        val handle = analyticsHandle ?: return
        runCatching {
            val js = js("({})")
            params.forEach { (k, v) -> js[k] = v }
            FirebaseAnalyticsLib.logEvent(handle, name, js)
        }
    }

    /** Record an error. Firebase Analytics for web has no Crashlytics
     *  equivalent, so this lands as a regular event named
     *  `app_exception` with `kind` (the source / category — segments
     *  the console view) and a short message.
     *
     *  Takes plain strings rather than a [Throwable] because every
     *  caller already has them — wrapping in a synthetic exception
     *  just to satisfy the signature collapses every `error_type` to
     *  `RuntimeException` and erases the segmentation. */
    fun recordError(kind: String, message: String?, context: Map<String, Any?> = emptyMap()) {
        val handle = analyticsHandle ?: return
        runCatching {
            val js = js("({})")
            js.error_type = kind
            js.error_message = (message ?: "").take(100)
            context.forEach { (k, v) -> js[k] = v }
            FirebaseAnalyticsLib.logEvent(handle, "app_exception", js)
        }
    }
}

/**
 * Google Tag Manager's `dataLayer` (container loaded in index.html);
 * GTM triggers match on the `event` key. Independent of [Analytics] on
 * purpose: GTM tags must fire even when Firebase isn't configured.
 */
object GtmDataLayer {
    fun push(event: String) {
        runCatching {
            val w = window.asDynamic()
            if (w.dataLayer == null) w.dataLayer = arrayOf<Any?>()
            val entry = js("({})")
            entry.event = event
            w.dataLayer.push(entry)
        }
    }
}

// ─── Bucketing helpers ────────────────────────────────────────────────
// Firebase counts every distinct event-param value as a separate
// dimension. Sending raw numeric metrics (words=14_823, credits=37)
// explodes the dimension count and breaks segmentation in the console.
// These helpers map continuous values to a small fixed set of buckets
// so reports stay readable. Bucket names are intentionally cheap to
// rename later — they exist only in the analytics layer.

fun bucketWordCount(words: Int): String = when {
    words <= 0       -> "0"
    words < 1_000    -> "<1k"
    words < 5_000    -> "1k-5k"
    words < 15_000   -> "5k-15k"
    words < 50_000   -> "15k-50k"
    words < 150_000  -> "50k-150k"
    else             -> "150k+"
}

fun bucketCredits(credits: Int): String = when {
    credits <= 0   -> "0"
    credits <= 10  -> "1-10"
    credits <= 50  -> "11-50"
    else           -> "51+"
}

fun bucketScore(score: Double?): String = when {
    score == null  -> "none"
    score < 3.0    -> "<3"
    score < 5.0    -> "3-5"
    score < 7.0    -> "5-7"
    score < 9.0    -> "7-9"
    else           -> "9+"
}

fun bucketFreeShortRemaining(n: Int): String = when {
    n <= 0  -> "0"
    n <= 2  -> "1-2"
    n <= 5  -> "3-5"
    else    -> "6+"
}

fun bucketDurationSeconds(sec: Int): String = when {
    sec <= 0    -> "0"
    sec < 30    -> "<30s"
    sec < 60    -> "30-60s"
    sec < 180   -> "1-3m"
    sec < 600   -> "3-10m"
    else        -> "10m+"
}

/**
 * Resolve [FirebaseConfig] from the build-time constants in
 * [BuildFirebaseConfig], which Gradle generates from root
 * `local.properties` (`FIREBASE_*` keys) on every compile. Returns
 * null when the constants are empty — `Main.main()` treats that as
 * "skip Analytics.init" so the app still boots cleanly.
 */
fun resolveFirebaseConfig(): FirebaseConfig? {
    if (BuildFirebaseConfig.API_KEY.isBlank() || BuildFirebaseConfig.PROJECT_ID.isBlank()) return null
    return FirebaseConfig(
        apiKey = BuildFirebaseConfig.API_KEY,
        authDomain = BuildFirebaseConfig.AUTH_DOMAIN,
        projectId = BuildFirebaseConfig.PROJECT_ID,
        storageBucket = BuildFirebaseConfig.STORAGE_BUCKET,
        messagingSenderId = BuildFirebaseConfig.MESSAGING_SENDER_ID,
        appId = BuildFirebaseConfig.APP_ID,
        measurementId = BuildFirebaseConfig.MEASUREMENT_ID.takeIf { it.isNotBlank() },
    )
}
